import React, { useEffect, useState, createContext, useContext } from "react";
import { useSignalR } from "./SignalRProvider";

// Create context for children to use lock state
const LockContext = createContext(null);
export const useLockState = () => useContext(LockContext);

export const LockCheck = ({ entity, entityId, children, lockChildElements=true }) => {
  const { isConnected, clientIp, lastEvent, openEntity, closeEntity, overrideLock, userGuid, connectionId } = useSignalR() || {};
  const [lockState, setLockState] = useState({isConnected: false, isLocked: false, lockedBy: { userGuid: null, connectionId: null, clientIp: null, username: null }, currentUser: { userGuid: userGuid, connectionId: connectionId, clientIp: clientIp }});
  const [lockOverride, setLockOverride] = useState({isOn: false, message: ''});
  const [updateClients, setUpdateClients] = useState(false);

  // 🔒 Open/close entity on mount/unmount
  useEffect(() => {
    if (isConnected && connectionId && entityId) {
      openEntity?.(entity, entityId);
    }
    return () => {
      if (isConnected && entityId) {
        closeEntity?.(entity, entityId);
      }
    };
  }, [isConnected, entity, entityId, openEntity, closeEntity, userGuid, connectionId]);

  // 🔄 Listen for lock/unlock events
  useEffect(() => {
    if (!lastEvent || !entityId) return;

    if (lastEvent.type === "EntityLocked" && lastEvent.data.entity === entity && lastEvent.data.entityId.toString() === entityId.toString() && connectionId) {      
      if (lastEvent.data.userGuid !== userGuid) {
        // Locked by another user
        setLockState({
          isConnected,
          isLocked: true,
          lockedBy: { userGuid: lastEvent.data.userGuid, connectionId: lastEvent.data.connectionId, clientIp: lastEvent.data.clientIp, username: lastEvent.data.username },
          currentUser: { userGuid, connectionId, clientIp }
        });
      }
      else if (lastEvent.data.connectionId === connectionId) {
        // Locked by me in this tab
        setLockState({
          isConnected,
          isLocked: false,
          lockedBy: { userGuid, connectionId, clientIp, username: lastEvent.data.username },
          currentUser: { userGuid, connectionId, clientIp }
        });
      } else if (lastEvent.data.clientIp === clientIp) {
        // Locked by me in another tab
        setLockState({
          isConnected,
          isLocked: true,
          lockedBy: { userGuid, connectionId: lastEvent.data.connectionId, clientIp, username: lastEvent.data.username },
          currentUser: { userGuid, connectionId, clientIp }
        });
      } else if (lastEvent.data.clientIp !== clientIp) {
        // Locked by me in another device
        setLockState({
          isConnected,
          isLocked: true,
          lockedBy: { userGuid, connectionId: lastEvent.data.connectionId, clientIp: lastEvent.data.clientIp, username: lastEvent.data.username },
          currentUser: { userGuid, connectionId, clientIp }
        });
      }
    }

    if (lastEvent.type === "EntityUnlocked" && lastEvent.data.entity === entity && lastEvent.data.entityId.toString() === entityId.toString()) {
      setLockState({
        isConnected,
        isLocked: false,
        lockedBy: { userGuid: null, connectionId: null, clientIp: null, username: null },
        currentUser: { userGuid, connectionId, clientIp }
      });      

      if (isConnected) {
        openEntity?.(entity, entityId, true);
      }
    }

    if (lastEvent.type === "LockOverridden" && lastEvent.data.entity === entity && lastEvent.data.entityId.toString() === entityId.toString()) {      
      // set updateClients to true. So the child components can listen to updateClients via UseEffect and do something when it is true
      setUpdateClients(true);
    }
  }, [lastEvent, entity, entityId, userGuid, connectionId, isConnected, openEntity]);

  const effectiveLocked = lockState.isLocked || lockOverride.isOn;

  const notifyLockOverride = () => {
    // triggers an event in SignalR that notifies all users who have opened the entity that the lock has been overridden
    overrideLock?.(entity, entityId);
  }

  // Provide lockState to all descendants
  return (
    <LockContext.Provider value={{...lockState, isLocked: effectiveLocked, notifyLockOverride, lockOverride, setLockOverride, updateClients, setUpdateClients}}>
      <div className={ (effectiveLocked && lockChildElements) ? "locked" : "unlocked" }>
        {children}
      </div>
    </LockContext.Provider>
  );
};
